import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { PublishQuestionnaireResult, QuestionnaireFormDefinition } from '@stores/QuestionnaireAdminStore';
import { PublishQuestionnaireAction } from '@actions/Admin/Questionnaire/PublishQuestionnaireAction';
import {
  QuestionnaireAdminViewCreator,
  useQuestionnaireVersion,
  useQuestionnaireVersions,
} from '@app/view-creator/Admin/QuestionnaireAdminViewCreator';
import { invalidateQuestionnaireAdminQueries } from '@utils/cacheInvalidation';
import {
  ChangedItem,
  changedPublishedItems,
  emptyDefinition,
  MeaningAnswers,
  sameDefinition,
  withMeaningAnswers,
} from '@app/components/Admin/Questionnaires/questionnaireDefinitionEditing';

export interface EditorMessage {
  severity: 'success' | 'info' | 'error';
  text: string;
}

/**
 * Runs the questionnaire editor. A formId of 0 creates a new questionnaire. Edits stay on screen until Publish;
 * nothing is saved before then.
 */
export const useQuestionnaireEditorController = (formId: number) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isNew = formId <= 0;

  const versionsQuery = useQuestionnaireVersions(formId);
  const latestVersionId = versionsQuery.data?.[0]?.id ?? null;
  const latestVersionQuery = useQuestionnaireVersion(latestVersionId);
  const published = isNew ? null : (latestVersionQuery.data?.definition ?? null);

  const [definition, setDefinition] = useState<QuestionnaireFormDefinition>(emptyDefinition());
  const [changedItems, setChangedItems] = useState<ChangedItem[]>([]);
  const [meaningAnswers, setMeaningAnswers] = useState<MeaningAnswers>({});
  const [changeReason, setChangeReason] = useState('');
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [message, setMessage] = useState<EditorMessage | null>(null);
  const [viewingVersionId, setViewingVersionId] = useState<number | null>(null);

  // Start from the latest published version, and again whenever a newer one is published.
  useEffect(() => {
    if (published) setDefinition(published);
  }, [latestVersionQuery.data?.id]);

  const unpublishedChanges = useMemo(
    () => !sameDefinition(definition, published ?? emptyDefinition()),
    [definition, published]
  );

  const publish = async (toPublish: QuestionnaireFormDefinition) => {
    setPublishing(true);
    const response = await new PublishQuestionnaireAction().Execute(toPublish);
    setPublishing(false);
    setPublishDialogOpen(false);

    if (response.status !== 'SUCCESS') {
      setMessage({ severity: 'error', text: response.statusMessage });
      return;
    }
    const result = response.result as PublishQuestionnaireResult;
    setMessage({ severity: result.changed ? 'success' : 'info', text: response.statusMessage });
    invalidateQuestionnaireAdminQueries(queryClient);

    if (isNew) {
      const version = await new QuestionnaireAdminViewCreator().readVersion(result.formVersionId);
      navigate(`/admin/questionnaires/${version.formId}`, { replace: true });
    }
  };

  // A first version publishes straight away. A later one first asks why it was made, and about meaning if any
  // published item changed.
  const handlePublish = async () => {
    setMessage(null);
    if (!published) {
      await publish(withMeaningAnswers(definition, {}));
      return;
    }
    setChangedItems(changedPublishedItems(published, definition));
    setMeaningAnswers({});
    setChangeReason('');
    setPublishDialogOpen(true);
  };

  const handleMeaningAnswer = (itemCode: string, changesMeaning: boolean) =>
    setMeaningAnswers((answers) => ({ ...answers, [itemCode]: changesMeaning }));

  const handleConfirmPublish = async () =>
    await publish({ ...withMeaningAnswers(definition, meaningAnswers), changeReason: changeReason });

  const handleDiscard = () => {
    setDefinition(published ?? emptyDefinition());
    setMessage(null);
  };

  return {
    isNew,
    loading: !isNew && (versionsQuery.isLoading || latestVersionQuery.isLoading),
    loadFailed: !isNew && (versionsQuery.isError || latestVersionQuery.isError),
    versions: versionsQuery.data ?? [],
    definition,
    published,
    unpublishedChanges,
    message,
    publishing,
    // The number the next version gets; versions are newest first.
    nextVersion: (versionsQuery.data?.[0]?.version ?? 0) + 1,
    changedItems,
    meaningAnswers,
    changeReason,
    publishDialogOpen,
    viewingVersionId,
    setDefinition,
    setViewingVersionId,
    setChangeReason,
    handlePublish,
    handleMeaningAnswer,
    handleConfirmPublish,
    handleCancelPublish: () => setPublishDialogOpen(false),
    handleDiscard,
  };
};
